import * as Yup from "yup";
import { useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
// form
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
// @mui
import { alpha } from "@mui/material/styles";
import { Box, Link, Stack, Alert, IconButton, InputAdornment, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
// routes
import { PATH_AUTH } from "../../../routes/paths";
// hooks
import useAuth from "../../../hooks/useAuth";
import useIsMountedRef from "../../../hooks/useIsMountedRef";
// components
import Iconify from "../../../components/Iconify";
import {
  FormProvider,
  RHFTextField,
  RHFCheckbox,
  RHFPhoneField,
} from "../../../components/hook-form";

// ----------------------------------------------------------------------

export default function LoginForm() {
  const { login } = useAuth();

  const isMountedRef = useIsMountedRef();

  const [showPassword, setShowPassword] = useState(false);

  const LoginSchema = Yup.object().shape({
    phone: Yup.string().required("this field is required"),
    password: Yup.string().required("Password is required"),
  });

  const defaultValues = {
    phone: "",
    password: "",
    remember: true,
  };

  const methods = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });

  const {
    reset,
    setError,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = methods;

  const navigate = useNavigate();

  function extractErrorMessage(error) {
    // Check if `response` and `data` exist
    if (error.response?.data) {
      const { data } = error.response;

      if (typeof data === "string") {
        return data;
      }

      if (Array.isArray(data)) {
        return data[0];
      }

      if (data.non_field_errors?.length) {
        return data.non_field_errors[0];
      }

      if (data.detail) {
        return data.detail;
      }

      if (data.phone?.length) {
        return data.phone[0];
      }
    }

    // Check if `response` and `error` exist
    if (error.response?.error) {
      const { error: errData } = error.response;

      if (typeof errData === "string") {
        return errData;
      }

      if (Array.isArray(errData)) {
        return errData[0];
      }

      if (errData.detail) {
        return errData.detail;
      }
    }

    // Default fallback error message
    return "An error occurred. Please try again.";
  }


  const onSubmit = async (data) => {
    try {
      await login(data.phone, data.password);
      return navigate("/");
    } catch (error) {
      console.log(error);
      const err = extractErrorMessage(error)
      console.log(err)
      reset();
      if (isMountedRef.current) {
        setError("afterSubmit", {
          ...error,
          message: err || "errorOccurred",
        });
      }
    }
  };

  const rememberLabel = (
    <Stack spacing={0.2}>
      <Typography variant="subtitle2" sx={{ color: "text.primary", lineHeight: 1.15 }}>
        Souviens-toi de moi
      </Typography>
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        Reste connecté sur cet appareil
      </Typography>
    </Stack>
  );

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        {!!errors.afterSubmit && (
          <Alert severity="error">{errors.afterSubmit.message}</Alert>
        )}

        <RHFPhoneField type="tel" name="phone" label="Phone" />

        <RHFTextField
          name="password"
          label="Password"
          type={showPassword ? "text" : "password"}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  <Iconify
                    icon={showPassword ? "eva:eye-fill" : "eva:eye-off-fill"}
                  />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      <Box
        sx={{
          mt: 2.25,
          mb: 2.5,
          p: 1.1,
          borderRadius: 3,
          bgcolor: (theme) => alpha(theme.palette.background.neutral, 0.35),
          border: (theme) => `1px solid ${alpha(theme.palette.grey[500], 0.12)}`,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
        >
          <RHFCheckbox
            name="remember"
            label={rememberLabel}
            sx={{
              m: 0,
              py: 0.2,
              pl: 0.25,
              pr: 1,
              borderRadius: 2.5,
              alignItems: "center",
            }}
          />

          <Link
            component={RouterLink}
            variant="subtitle2"
            to={PATH_AUTH.resetPassword}
            underline="none"
            sx={{
              px: 1.25,
              py: 1,
              borderRadius: 2.5,
              color: "primary.main",
              alignSelf: { xs: "flex-start", sm: "center" },
              "&:hover": {
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
              },
            }}
          >
            Mot de passe oublié?
          </Link>
        </Stack>
      </Box>

      <LoadingButton
        fullWidth
        size="large"
        type="submit"
        variant="contained"
        loading={isSubmitting}
        sx={{
          height: 58,
          borderRadius: 3.5,
          fontSize: 18,
          fontWeight: 700,
          letterSpacing: "-0.01em",
          background: "linear-gradient(135deg, #6D34E6 0%, #8B3FF3 100%)",
          boxShadow: "0 16px 36px rgba(109, 52, 230, 0.28)",
          "&:hover": {
            background: "linear-gradient(135deg, #5E27D8 0%, #7F35EC 100%)",
            boxShadow: "0 18px 40px rgba(109, 52, 230, 0.32)",
          },
        }}
      >
        Se connecter
      </LoadingButton>
    </FormProvider>
  );
}
